import React, { useState } from 'react';
import type { HtmlSplitData, Page, Component } from '../../../schemas/validation';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { NumberInput } from '../shared/NumberInput';
import { RichTextEditor } from '../../RichTextEditor';

interface HtmlSplitEditorProps {
  formData: HtmlSplitData;
  setFormData: (data: HtmlSplitData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

type EmbedTab = 'custom' | 'iframe' | 'video';

export const HtmlSplitEditor: React.FC<HtmlSplitEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();
  const colorFields = getColorFieldsForComponent('html-split', themeColors);
  const embedType = (data.embed?.embedType as EmbedTab) || 'custom';
  const [activeEmbedTab, setActiveEmbedTab] = useState<EmbedTab>(
    embedType === 'iframe' || embedType === 'video' ? embedType : 'custom'
  );

  const handleButtonsChange = (buttons: ButtonData[]) => {
    setFormData({ ...data, buttons: buttons as HtmlSplitData['buttons'] });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: {
        ...data.customColors,
        [key]: value,
      },
    });
  };

  const updateEmbed = (patch: Record<string, unknown>) => {
    setFormData({
      ...data,
      embed: {
        ...(data.embed || { embedType: 'custom' }),
        ...patch,
      } as HtmlSplitData['embed'],
    });
  };

  const setEmbedTab = (tab: EmbedTab) => {
    setActiveEmbedTab(tab);
    updateEmbed({ embedType: tab });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Text Content</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
            <input
              type="text"
              value={data.tagline || ''}
              onChange={e => setFormData({ ...data, tagline: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Optional tagline"
              maxLength={100}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={data.title || ''}
              onChange={e => setFormData({ ...data, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Section title"
              maxLength={200}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
            <RichTextEditor
              content={data.subtitle || ''}
              onChange={subtitle => setFormData({ ...data, subtitle })}
              placeholder="Supporting text"
              minHeight="120px"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <ButtonsEditor
          buttons={(data.buttons || []) as ButtonData[]}
          onChange={handleButtonsChange}
          pages={pages}
          isLoadingPages={isLoadingPages}
          currentPageComponents={currentPageComponents}
        />
      </div>

      <div className="border-t pt-6 space-y-4">
        <h3 className="text-md font-medium text-gray-900 mb-2">Layout</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">HTML Position</label>
            <select
              value={data.layout || 'html-right'}
              onChange={e =>
                setFormData({
                  ...data,
                  layout: e.target.value as 'html-left' | 'html-right',
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="html-right">HTML on right</option>
              <option value="html-left">HTML on left</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Text Alignment</label>
            <select
              value={data.alignment || 'left'}
              onChange={e =>
                setFormData({
                  ...data,
                  alignment: e.target.value as 'left' | 'center' | 'right',
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
            <select
              value={data.size || 'large'}
              onChange={e =>
                setFormData({
                  ...data,
                  size: e.target.value as 'small' | 'medium' | 'large',
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              HTML Min Height (px)
            </label>
            <NumberInput
              min={200}
              max={2000}
              value={data.htmlMinHeight ?? 480}
              fallback={480}
              onChange={htmlMinHeight => setFormData({ ...data, htmlMinHeight })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Keeps calendars and embeds from collapsing the layout.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Text Width (%)</label>
          <NumberInput
            min={20}
            max={80}
            value={data.textWidth || 50}
            fallback={50}
            onChange={textWidth => {
              const htmlWidth = 100 - textWidth;
              setFormData({ ...data, textWidth, htmlWidth });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            HTML width will automatically adjust to {100 - (data.textWidth || 50)}%
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.stackOnMobile !== false}
              onChange={e => setFormData({ ...data, stackOnMobile: e.target.checked })}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Stack on mobile</span>
          </label>
          {data.stackOnMobile !== false && (
            <label className="flex items-center space-x-2 cursor-pointer pl-6">
              <input
                type="checkbox"
                checked={!!data.mobileHtmlFirst}
                onChange={e => setFormData({ ...data, mobileHtmlFirst: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Show HTML panel first on mobile</span>
            </label>
          )}
        </div>
      </div>

      <div className="border-t pt-6 space-y-4">
        <h3 className="text-md font-medium text-gray-900">Custom HTML / Embed</h3>
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          {(
            [
              ['custom', 'Custom HTML'],
              ['iframe', 'Iframe'],
              ['video', 'Video'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setEmbedTab(id)}
              className={`px-3 py-1.5 text-sm rounded-md ${
                activeEmbedTab === id
                  ? 'bg-primary-100 text-primary-800 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeEmbedTab === 'custom' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">HTML</label>
              <textarea
                value={data.embed?.custom?.html || ''}
                onChange={e =>
                  updateEmbed({
                    embedType: 'custom',
                    custom: {
                      ...(data.embed?.custom || {}),
                      html: e.target.value,
                    },
                  })
                }
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="<p>Paste in custom HTML here</p>"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CSS (optional)</label>
              <textarea
                value={data.embed?.custom?.css || ''}
                onChange={e =>
                  updateEmbed({
                    embedType: 'custom',
                    custom: {
                      ...(data.embed?.custom || {}),
                      css: e.target.value,
                    },
                  })
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="/* Optional styles */"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                JavaScript (optional)
              </label>
              <textarea
                value={data.embed?.custom?.javascript || ''}
                onChange={e =>
                  updateEmbed({
                    embedType: 'custom',
                    custom: {
                      ...(data.embed?.custom || {}),
                      javascript: e.target.value,
                    },
                  })
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="// Optional script"
              />
            </div>
          </div>
        )}

        {activeEmbedTab === 'iframe' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Iframe URL</label>
              <input
                type="url"
                value={(data.embed?.iframe as { src?: string } | undefined)?.src || ''}
                onChange={e =>
                  updateEmbed({
                    embedType: 'iframe',
                    iframe: {
                      ...((data.embed?.iframe as object) || {}),
                      src: e.target.value,
                      title:
                        (data.embed?.iframe as { title?: string } | undefined)?.title || 'Embed',
                      allowFullscreen: true,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={(data.embed?.iframe as { title?: string } | undefined)?.title || ''}
                onChange={e =>
                  updateEmbed({
                    embedType: 'iframe',
                    iframe: {
                      ...((data.embed?.iframe as object) || {}),
                      title: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Accessible title"
              />
            </div>
          </div>
        )}

        {activeEmbedTab === 'video' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
              <select
                value={
                  (data.embed?.video as { platform?: string } | undefined)?.platform || 'youtube'
                }
                onChange={e =>
                  updateEmbed({
                    embedType: 'video',
                    video: {
                      ...((data.embed?.video as object) || {}),
                      platform: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="youtube">YouTube</option>
                <option value="vimeo">Vimeo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video ID</label>
              <input
                type="text"
                value={(data.embed?.video as { videoId?: string } | undefined)?.videoId || ''}
                onChange={e =>
                  updateEmbed({
                    embedType: 'video',
                    video: {
                      ...((data.embed?.video as object) || { platform: 'youtube' }),
                      videoId: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g. dQw4w9WgXcQ"
              />
            </div>
          </div>
        )}
      </div>

      <div className="border-t pt-6">
        <ColorCustomizationPanel
          colorFields={colorFields}
          customColors={data.customColors || {}}
          onUpdateColor={updateCustomColor}
        />
      </div>
    </div>
  );
};

export default HtmlSplitEditor;
